import { makeStyles } from "tss-react/mui"
import { Stack } from "@mui/material"
import { faHeart, faMagnifyingGlass, faUtensils } from "@fortawesome/pro-regular-svg-icons"

import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { tailwindPalette } from "../../../../../../modules/theme/tailwindPalette"
import { EmptyContent } from "../../../../skeleton/EmptyContent"

import type {
    TakeawayCartItem,
    TakeawayProduct,
    TakeawayProductGroup as TakeawayProductGroupType,
} from "../takeawayTypes"
import { TakeawayProductGroup } from "./TakeawayProductGroup"
import { TakeawayMenuContentSkeleton } from "./TakeawayMenuContentSkeleton"

const useStyles = makeStyles()(() => ({
    emptyWrap: {
        padding: "2rem 1rem",
        height: "100%",
    },
}))

type TakeawayMenuContentProps = Readonly<{
    isLoading: boolean
    allGroups: TakeawayProductGroupType[]
    displayGroups: TakeawayProductGroupType[]
    showFavorites: boolean
    favoriteIds: ReadonlySet<number>
    searchRegex: RegExp | undefined
    cart: TakeawayCartItem[]
    onQuantityChange: (product: TakeawayProduct, quantity: number) => void
    onFavoriteToggle: (product: TakeawayProduct, makeFavorite: boolean) => Promise<boolean>
    onDetailsClick: (product: TakeawayProduct) => void
}>

export function TakeawayMenuContent({
    isLoading,
    allGroups,
    displayGroups,
    showFavorites,
    favoriteIds,
    searchRegex,
    cart,
    onQuantityChange,
    onFavoriteToggle,
    onDetailsClick,
}: TakeawayMenuContentProps) {
    const { classes } = useStyles()

    const getCartItem = (productId: number) => cart.find((item) => item.product.id === productId)

    if (isLoading) return <TakeawayMenuContentSkeleton />

    if (!allGroups.length) {
        return (
            <Stack className={classes.emptyWrap}>
                <LocalizedStrict
                    id="takeaway-menu-no-products-for-date-empty"
                    attrs={{ title: true, description: true }}
                >
                    <EmptyContent
                        icon={faUtensils}
                        backgroundColor={tailwindPalette.slate[400]}
                        title="No products available"
                        description="There are no products available at this time"
                    />
                </LocalizedStrict>
            </Stack>
        )
    }

    if (!displayGroups.length) {
        return (
            <Stack className={classes.emptyWrap}>
                {showFavorites ? (
                    <LocalizedStrict id="takeaway-menu-no-favorites-empty" attrs={{ title: true, description: true }}>
                        <EmptyContent
                            icon={faHeart}
                            backgroundColor={tailwindPalette.pink[400]}
                            title="No favorites yet"
                            description="Tap the heart icon on a product to add it as a favorite"
                        />
                    </LocalizedStrict>
                ) : (
                    <LocalizedStrict
                        id="takeaway-menu-no-search-results-empty"
                        attrs={{ title: true, description: true }}
                    >
                        <EmptyContent
                            icon={faMagnifyingGlass}
                            backgroundColor={tailwindPalette.slate[400]}
                            title="No results"
                            description="Try adjusting your search"
                        />
                    </LocalizedStrict>
                )}
            </Stack>
        )
    }

    return (
        <Stack gap="2rem">
            {displayGroups.map((group) => (
                <TakeawayProductGroup
                    key={group.id}
                    group={group}
                    getCartItem={getCartItem}
                    favoriteIds={favoriteIds}
                    searchRegex={searchRegex}
                    onQuantityChange={onQuantityChange}
                    onFavoriteToggle={onFavoriteToggle}
                    onDetailsClick={onDetailsClick}
                />
            ))}
        </Stack>
    )
}
